import { H2, P, Ul, Callout, DocTable, Code } from '../../components/docs/primitives';

export default function Safety() {
  return (
    <>
      <H2 id="installing">What happens at install</H2>
      <P>
        Before anything is unpacked, StreamNook verifies the chain end to end and fails closed at
        the first mismatch:
      </P>
      <Ul>
        <li>The index's signature verifies against its pinned operator key.</li>
        <li>The artifact's hash matches the index entry.</li>
        <li>The author's detached signature verifies, against a key pinned on first install.</li>
        <li>The manifest inside the artifact matches the index entry exactly.</li>
      </Ul>
      <P>
        Only then does the consent dialog appear, showing the plugin's name, author, version,
        source, capability label, and every permission it requests. No plugin code executes before you
        confirm.
      </P>

      <H2 id="consent">The consent dialog</H2>
      <P>
        Permissions are rendered one line each, in plain language, straight from what the plugin
        requested. A few examples of the exact strings:
      </P>
      <DocTable
        head={['Requested', 'What you see']}
        rows={[
          ['Watch events', '"Knows which channel you are watching"'],
          ['Followed channels', '"Can ask for your list of live followed channels"'],
          ['Notifications', '"Can show you notifications"'],
          [
            'Networking',
            '"Makes its own network connections, to Twitch and elsewhere. StreamNook does not see or control this traffic"',
          ],
          [
            'Twitch credential',
            '"Can request your Twitch login token. With it, this plugin can act as your Twitch account"',
          ],
        ]}
      />
      <P>
        You can grant a subset; whatever you do not grant is denied at runtime. An update that
        asks for new permissions or a higher capability label re-runs the full consent flow.
      </P>
      <Callout tone="warning">
        Advanced installs never collapse into a simple confirm. The dialog names the plugin,
        states in plain language what it can do with your account, and requires ticking a checkbox
        before the install button enables. A single click can never turn one on.
      </Callout>

      <H2 id="credentials">Your Twitch login</H2>
      <P>
        Granting a plugin permission to ask for your login is not the same as handing it over. The
        first time a plugin actually requests it, StreamNook stops and asks you, stating plainly
        that a token lets the plugin act as your Twitch account. The choices are Allow, Allow and
        don't ask again, or Deny.
      </P>
      <P>
        Every successful handover is appended to that plugin's audit log with a timestamp, stored
        on your machine and viewable from the plugins page.
      </P>

      <H2 id="community-sources">Community sources</H2>
      <P>
        Adding a source is itself a consented action. The dialog shows the source URL and the
        operator's key fingerprint, and is explicit that StreamNook does not review, host, or
        endorse what a community source lists, so you should only add sources you trust. The
        fingerprint is pinned: future updates from that source must be signed with the same key or
        they fail loudly.
      </P>

      <H2 id="controls">Your controls</H2>
      <Ul>
        <li>Disabling a plugin shuts its process down immediately. Uninstalling removes it.</li>
        <li>
          Revoking credential access takes effect immediately; the plugin's next request fails
          with <Code>consent_denied</Code>.
        </li>
        <li>The per-plugin audit log of credential handovers is always viewable.</li>
        <li>
          A plugin delisted from its index stays installed (you chose it) but is flagged with the
          reason, and its updates stop.
        </li>
      </Ul>
    </>
  );
}
